import React, { ReactElement, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Controller, useForm } from 'react-hook-form';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useUpdateClinicSettingsTeamUserMutation,
  Role,
  useUpsertUserToClinicMutation,
  ClinicSettingsTeamUserFragment,
  CareProgramLaunchStatus,
  UserUpdateInput,
} from 'shared/types/graphql';
import { email as emailAddressRegex, phone as phoneNumberRegex } from 'shared/utils/validation';
import { useValidation, ValidationResponseCodes } from '../../../../shared/hooks/useValidation';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Select, SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu/components/MenuItem';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';

const prefixOptions = ['Ms', 'Mrs', 'Mr', 'Dr'];

const allowedCareProgramStatuses: CareProgramLaunchStatus[] = [
  CareProgramLaunchStatus.Prelaunch,
  CareProgramLaunchStatus.Live,
  CareProgramLaunchStatus.Churning,
];

interface IUserForm {
  namePrefix: string;
  firstName: string;
  lastName: string;
  nameSuffix: string;
  emailAddress: string;
  phoneNumber: string;
  role: string;
}

interface IEditUserModalProps {
  clinicId?: string;
  onAddUserSuccess: () => void;
  onEditUserSuccess: () => void;
  isOpen: boolean;
  onClose: () => void;
  user?: ClinicSettingsTeamUserFragment;
}

const EditUserModal = ({
  clinicId,
  onAddUserSuccess,
  onEditUserSuccess,
  isOpen,
  onClose,
  user,
}: IEditUserModalProps): ReactElement => {
  const toast = useToast();
  const { currentClinic, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { validatePhoneNumber } = useValidation();
  const [updateUser, { loading: isUpdatingUser }] = useUpdateClinicSettingsTeamUserMutation();
  const [upsertUserToClinic, { loading: isCreatingUser }] = useUpsertUserToClinicMutation();
  const { isMobile } = useResolutionProvider();

  const roles = useMemo(() => currentClinic?.roles || [], [currentClinic?.roles]);
  const adminRole = useMemo(() => roles.find(({ role }) => role === Role.Admin), [roles]);

  const currentUserRoles = user?.vetInfo?.roles;

  const isAddingUser = !user;
  const isSavingUser = isUpdatingUser || isCreatingUser;

  const { handleSubmit, control, register, reset, getValues, setValue, errors } = useForm<IUserForm>({
    mode: 'onBlur',
    validateCriteriaMode: 'all',
  });

  useEffect(() => {
    if (!!user) {
      const { firstName, lastName, namePrefix, nameSuffix, email, phone, vetInfo } = user;
      const role = vetInfo?.roles?.find((role) => !!role) || adminRole;
      reset({
        firstName,
        lastName,
        namePrefix: namePrefix || '',
        nameSuffix: nameSuffix || '',
        emailAddress: email,
        phoneNumber: (phone !== 'undefined' && phone) || '',
        role: role?.id || '',
      });
    } else {
      reset({
        firstName: '',
        lastName: '',
        namePrefix: '',
        nameSuffix: '',
        emailAddress: '',
        phoneNumber: '',
        role: adminRole?.id || '',
      });
    }
  }, [user, reset, adminRole]);

  const handleSaveUser = async (data: IUserForm): Promise<void> => {
    if (isSavingUser) return;

    const { firstName, lastName, namePrefix, nameSuffix, emailAddress, phoneNumber, role } = data;

    try {
      if (isAddingUser) {
        if (!clinicId) {
          return;
        }

        await upsertUserToClinic({
          variables: {
            data: {
              clinicId,
              email: emailAddress,
              firstName,
              lastName,
              namePrefix,
              nameSuffix,
              phone: phoneNumber,
              roleId: role,
            },
          },
        });
        onAddUserSuccess();
      } else {
        let vetInfo: UserUpdateInput['vetInfo'];
        if (currentUserRoles && currentUserRoles[0] && currentUserRoles[0].id) {
          const currentRoleID = currentUserRoles[0]?.id;
          if (currentRoleID !== role) {
            vetInfo = {
              update: {
                data: {
                  roles: {
                    connect: [{ id: role }],
                    disconnect: role ? roles.filter(({ id }) => id !== role).map(({ id }) => ({ id })) : [],
                  },
                },
              },
            };
          }
        }

        await updateUser({
          variables: {
            where: { id: user?.id },
            data: {
              firstName,
              lastName,
              namePrefix,
              nameSuffix,
              phone: phoneNumber,
              type: user?.type,
              vetInfo,
            },
          },
        });
        onEditUserSuccess();
      }
      toast({
        title: `User successfully ${isAddingUser ? 'added' : 'saved'}`,
        status: 'success',
      });
      onClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toast({
        title: `There was a problem ${isAddingUser ? 'adding' : 'saving'} the user`,
        status: 'error',
      });
    }
  };

  const handlePhoneNumberBlur = (): void => {
    const phone = getValues('phoneNumber');
    if (!!phone) {
      setValue('phoneNumber', phone.replace(phoneNumberRegex, '($2) $3-$4'));
    }
  };

  const availableUserRoles: SelectOptionProps[] = useMemo(() => {
    const filteredRoles: SelectOptionProps[] = [];
    roles.forEach((_role) => {
      const { id, role } = _role;
      const roleLabel = role.split('_').join(' ');

      const isTeletriageAgentRole = role === Role.TeletriageAgent;

      // TeletriageAgent should only be set by admins and only be visible for Prelaunch/Live/Churning Care programs
      if (
        isTeletriageAgentRole &&
        hasAdminRoleAtCurrentClinic &&
        currentClinic?.carePlanProviderGroup &&
        allowedCareProgramStatuses.includes(currentClinic.carePlanProviderGroup.launchStatus)
      ) {
        filteredRoles.push({ value: id, label: roleLabel });
      }

      if (!isTeletriageAgentRole) {
        filteredRoles.push({ value: id, label: roleLabel });
      }
    });

    return filteredRoles;
  }, [roles, hasAdminRoleAtCurrentClinic, currentClinic?.carePlanProviderGroup]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalCloseButton />
      <ModalHeader>{isAddingUser ? 'Add' : 'Edit'} User</ModalHeader>
      <ModalBody>
        <Box as="form">
          <Flex direction={isMobile ? 'column' : 'row'} gap={3} my={6}>
            <Flex alignItems="flex-start" justifyContent="space-between" gap={3}>
              <Controller
                as={Select}
                name="namePrefix"
                control={control}
                options={prefixOptions.map((prefix) => ({ value: prefix, label: prefix }))}
                listProps={{
                  onSelect: (selectedItem: MenuItemProps): void => {
                    setValue('namePrefix', selectedItem.value || '');
                  },
                }}
                label="Prefix"
                labelStyle={{ fontWeight: 'semibold' }}
                formControlStyle={{ flex: '0 0 90px' }}
              />
              <Controller
                as={TextInput}
                name="firstName"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Please provide a first name',
                  },
                }}
                ref={register({
                  required: 'Please provide a first name',
                })}
                label="First Name"
                labelStyle={{ fontWeight: 'semibold' }}
                formControlStyle={{ flex: '3' }}
                isRequired
                errorText={`${errors.firstName?.message}`}
                isInvalid={!!errors.firstName}
              />
            </Flex>
            <Flex alignItems="flex-start" justifyContent="space-between" gap={3}>
              <Controller
                as={TextInput}
                name="lastName"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Please provide a last name',
                  },
                }}
                label="Last Name"
                labelStyle={{ fontWeight: 'semibold' }}
                formControlStyle={{ flex: '3' }}
                isRequired
                errorText={`${errors.lastName?.message}`}
                isInvalid={!!errors.lastName}
              />
              <Controller
                as={TextInput}
                name="nameSuffix"
                control={control}
                label="Suffix"
                labelStyle={{ fontWeight: 'semibold' }}
                formControlStyle={{ flex: '0 0 90px' }}
              />
            </Flex>
          </Flex>
          <Flex
            direction={isMobile ? 'column' : 'row'}
            alignItems="flex-start"
            justifyContent="space-between"
            my={6}
            gap={3}
          >
            <Controller
              as={TextInput}
              name="emailAddress"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Please provide an email address',
                },
                pattern: { value: emailAddressRegex, message: 'Please provide a valid email address' },
              }}
              isDisabled={!isAddingUser}
              label={
                <Flex alignItems="center">
                  <Text fontWeight="semibold" mr={1}>
                    Email Address <Text variant="danger">*</Text>
                  </Text>
                  {!isAddingUser && (
                    <Tooltip label="This user may edit their own email address on the Profile page.">
                      <Flex>
                        <Icon name="questionMarkCircle" variant="subtle" size="sm" />
                      </Flex>
                    </Tooltip>
                  )}
                </Flex>
              }
              formControlStyle={{ flex: '1' }}
              errorText={`${errors.emailAddress?.message}`}
              isInvalid={!!errors.emailAddress}
            />
            <Controller
              as={TextInput}
              name="phoneNumber"
              control={control}
              rules={{
                validate: {
                  isValidPhoneNumber: async (value): Promise<string | true | undefined> => {
                    let customMessage;
                    const { isValid, code, message } = await validatePhoneNumber(value);
                    if (!isValid && code === ValidationResponseCodes.PhoneNumberInvalidFormat) {
                      customMessage = 'Please provide a valid US phone number';
                    }
                    return !value || isValid || customMessage || message;
                  },
                },
              }}
              onBlur={handlePhoneNumberBlur}
              label="Phone Number"
              labelStyle={{ fontWeight: 'semibold' }}
              formControlStyle={{ flex: '1' }}
              errorText={`${errors.phoneNumber?.message}`}
              isInvalid={!!errors.phoneNumber}
            />
          </Flex>
          <Flex alignItems="flex-start" justifyContent="space-between" my={6} gap={3}>
            <Controller
              as={Select}
              name="role"
              control={control}
              options={availableUserRoles}
              listProps={{
                onSelect: (selectedItem: MenuItemProps): void => {
                  setValue('role', selectedItem.value || '');
                },
              }}
              label="Role"
              labelStyle={{ fontWeight: 'semibold' }}
              formControlStyle={{ flex: '0 0 calc(50% - 8px)' }}
              defaultValue={availableUserRoles[0]?.value}
            />
          </Flex>
        </Box>
      </ModalBody>
      <ModalFooterButtons
        submitText={isAddingUser ? 'Add User' : 'Save'}
        onSubmit={handleSubmit(handleSaveUser)}
        onCancel={(): void => {
          onClose();
        }}
        isLoading={isSavingUser}
      />
    </Modal>
  );
};

export default EditUserModal;
